import {
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { toolCallIdOfPart } from './subagent-part-ownership';

export type SubagentTaskDetails = {
  title?: string;
  prompt?: string;
  output?: string | Record<string, unknown>;
  error?: string;
};

const PROMPT_KEYS = ['description', 'query', 'prompt', 'task', 'objective'];
const TITLE_MAX_LENGTH = 60;

export function readSubagentTask({
  messages,
  toolCallId,
}: {
  messages: TerraUIMessage[];
  toolCallId: string;
}): SubagentTaskDetails {
  let details: SubagentTaskDetails | undefined;

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      if (
        part.type === TerraUIMessageDataPartName &&
        toolCallIdOfPart(part) === toolCallId
      ) {
        const next = taskDetailsFromData(part.data);

        details = details ? mergeTaskDetails(details, next) : next;
      }
    }
  }

  return details ?? {};
}

export function indexSubagentTasks({
  messages,
  parentToolCallIds,
}: {
  messages: readonly TerraUIMessage[];
  parentToolCallIds: readonly string[];
}): ReadonlyMap<string, SubagentTaskDetails> {
  const wanted = new Set(parentToolCallIds);
  const tasks = new Map<string, SubagentTaskDetails>();

  if (wanted.size === 0) {
    return tasks;
  }

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      const toolCallId = toolCallIdOfPart(part);

      if (
        !toolCallId ||
        !wanted.has(toolCallId) ||
        part.type !== TerraUIMessageDataPartName
      ) {
        continue;
      }

      const next = taskDetailsFromData(part.data);
      const current = tasks.get(toolCallId);

      tasks.set(toolCallId, current ? mergeTaskDetails(current, next) : next);
    }
  }

  return tasks;
}

function mergeTaskDetails(
  current: SubagentTaskDetails,
  next: SubagentTaskDetails,
): SubagentTaskDetails {
  return {
    title: next.title ?? current.title,
    prompt: next.prompt ?? current.prompt,
    output: next.output ?? current.output,
    error: next.error ?? current.error,
  };
}

function taskDetailsFromData(data: unknown): SubagentTaskDetails {
  const description = firstNonEmptyString({
    record: readRecord(data, 'input'),
    keys: PROMPT_KEYS,
  });
  const { title, prompt } = description
    ? splitTaskDescription(description)
    : { title: undefined, prompt: undefined };

  return {
    title,
    prompt,
    output: readOutput(data),
    error: firstNonEmptyString({ record: readRecord(data), keys: ['error'] }),
  };
}

function readRecord(
  value: unknown,
  key?: string,
): Record<string, unknown> | undefined {
  const target: unknown =
    key && value && typeof value === 'object' ? Reflect.get(value, key) : value;

  return target && typeof target === 'object' && !Array.isArray(target)
    ? (target as Record<string, unknown>)
    : undefined;
}

function firstNonEmptyString({
  record,
  keys,
}: {
  record: Record<string, unknown> | undefined;
  keys: string[];
}): string | undefined {
  return keys
    .map((key) => record?.[key])
    .find(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0,
    )
    ?.trim();
}

function readOutput(data: unknown): SubagentTaskDetails['output'] {
  const record = readRecord(data);
  const output = record?.output;

  if (typeof output === 'string') {
    return output.trim() ? output : undefined;
  }

  return readRecord(output);
}

function splitTaskDescription(description: string): {
  title?: string;
  prompt: string;
} {
  const lines = description.split('\n');
  const firstLineIndex = lines.findIndex((line) => line.trim().length > 0);
  const title = lines[firstLineIndex]?.replace(/^[#>*\-\s]+/, '').trim() ?? '';
  const rest = lines
    .slice(firstLineIndex + 1)
    .join('\n')
    .trim();
  const isSetApart =
    lines[firstLineIndex + 1]?.trim() === '' || title.length < rest.length;

  if (
    title.length === 0 ||
    title.length > TITLE_MAX_LENGTH ||
    rest.length === 0 ||
    !isSetApart
  ) {
    return { prompt: description };
  }

  return { title, prompt: rest };
}
